/**
 * CriticalSlideshowPage Component
 *
 * Full-screen component for displaying critical playlists (priority 5, prayer-based).
 * Provides minimal UI with date/time bar and auto-advancing slideshow.
 *
 * Priority: Shows above regular slideshow but below countdown/blank/makrooh.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Playlist } from "../../types/index";
import { DigitalAssetType } from "../../types/DigitalAssetTypes";
import { PrayerTimes } from "../../types/PrayerTypes";
import { ScreenData } from "../../types/ScreenTypes";
import { PrayerState } from "../../hooks/usePrayerStateManager";
import CachedImage from "../shared/CachedImage";
import VideoPlayerFactory from "../shared/VideoPlayerFactory";
import CurrentTimeBar from "../elements/CurrentTimeBar";
import "./CriticalSlideshowPage.css";

// ============================================================================
// INTERFACES
// ============================================================================

interface CriticalSlideshowPageProps {
  /** The critical playlist to display */
  playlist: Playlist;
  /** Current prayer times */
  prayerTimes: PrayerTimes;
  /** Tomorrow's prayer times (optional) */
  tomorrowPrayerTimes?: PrayerTimes | null;
  /** Screen configuration data */
  screenData: ScreenData;
  /** Current prayer state */
  prayerState: PrayerState;
  /** Masjid ID */
  masjidId: number;
  /** Callback when critical playlist should be deactivated */
  onCriticalComplete?: () => void;
  /** When true, play exactly one cycle then invoke onCriticalComplete */
  runSingleCycle?: boolean;
}

// ============================================================================
// COMPONENT IMPLEMENTATION
// ============================================================================

export default function CriticalSlideshowPage({
  playlist,
  prayerTimes,
  tomorrowPrayerTimes,
  screenData,
  prayerState,
  masjidId,
  onCriticalComplete,
  runSingleCycle = false,
}: CriticalSlideshowPageProps) {
  // ============================================================================
  // STATE MANAGEMENT
  // ============================================================================

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const items = playlist.items || [];
  const currentItem = items[currentIndex];
  const hasMultipleItems = items.length > 1;
  const assetType = currentItem?.asset?.type?.toLowerCase() || "";

  // DEBUG: Log asset details to diagnose image loading failures
  useEffect(() => {
    if (currentItem) {
      console.log(
        `🔍 [CriticalSlideshow] playlist="${playlist.name}" | item ${currentIndex + 1}/${items.length} | assetType="${assetType}" | url="${currentItem.asset?.url || "MISSING"}" | thumbnailUrl="${currentItem.asset?.thumbnailUrl || "MISSING"}" | fullItem:`,
        JSON.stringify(currentItem, null, 2),
      );
    }
  }, [currentItem, currentIndex, items.length, assetType, playlist.name]);

  // ============================================================================
  // AUTO-ADVANCE LOGIC
  // ============================================================================

  const advanceToNext = useCallback(() => {
    if (!hasMultipleItems) return;

    setIsTransitioning(true);

    // Short transition delay for smooth effect
    setTimeout(() => {
      setCurrentIndex((prevIndex) => {
        if (runSingleCycle) {
          return Math.min(prevIndex + 1, items.length - 1);
        }

        return (prevIndex + 1) % items.length;
      });
      setIsTransitioning(false);
    }, 300);
  }, [hasMultipleItems, items.length, runSingleCycle]);

  // Auto-advance timer
  useEffect(() => {
    if (!currentItem) return;
    if (!hasMultipleItems && !runSingleCycle) return;

    // Clear any existing timer
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    // Use item-specific duration or default to 10 seconds
    const duration = (currentItem.duration || 10) * 1000;

    console.log(
      `🚨 Critical item ${currentIndex + 1}/${items.length} will display for ${
        duration / 1000
      }s`,
    );

    timerRef.current = setTimeout(() => {
      const isLastItem = currentIndex >= items.length - 1;

      if (runSingleCycle && isLastItem) {
        onCriticalComplete?.();
        return;
      }

      if (hasMultipleItems) {
        advanceToNext();
      }
    }, duration);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    currentIndex,
    currentItem,
    hasMultipleItems,
    advanceToNext,
    items.length,
    onCriticalComplete,
    runSingleCycle,
  ]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleVideoEnded = useCallback(() => {
    console.log("🚨 Critical video ended - advancing to next item");
    advanceToNext();
  }, [advanceToNext]);

  // ============================================================================
  // CLEANUP
  // ============================================================================

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  // ============================================================================
  // VALIDATION
  // ============================================================================

  if (!playlist) {
    console.warn("🚨 CriticalSlideshowPage: No playlist provided");
    return null;
  }

  if (!items.length) {
    console.warn(
      `🚨 CriticalSlideshowPage: Playlist "${playlist.name}" has no items`,
    );
    return null;
  }

  if (!currentItem) {
    console.warn("🚨 CriticalSlideshowPage: No current item available");
    return null;
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="critical-slideshow-page">
      {/* Date/Time Bar at the top */}
      <div className="critical-time-bar">
        <CurrentTimeBar
          screenData={screenData}
          prayerTimes={prayerTimes}
          tomorrowPrayerTimes={tomorrowPrayerTimes}
        />
      </div>

      {/* Critical overlay background */}
      <div className="critical-slideshow-overlay">
        {/* Main content area with transition effects */}
        <div
          className={`critical-content ${
            isTransitioning ? "transitioning" : ""
          }`}
        >
          {assetType === DigitalAssetType.Poster ? (
            <div className="critical-slideshow-item image-item">
              <CachedImage
                src={currentItem.asset?.url || ""}
                alt={currentItem.asset?.title || "Critical Content"}
                className="critical-image"
                assetType={assetType}
                onError={advanceToNext}
              />
            </div>
          ) : assetType === DigitalAssetType.Video ? (
            <div className="critical-slideshow-item video-item">
              <VideoPlayerFactory
                item={currentItem}
                onEnded={handleVideoEnded}
                onError={(error) => {
                  console.error("Video error:", error);
                  advanceToNext();
                }}
                screenData={{
                  showPortraitPosterOnLandscape:
                    screenData.showPortraitPosterOnLandscape,
                  showLandscapePosterOnPortrait:
                    screenData.showLandscapePosterOnPortrait,
                  orientation: screenData.orientation,
                }}
                className="critical-video-player"
                fillMode="contain"
              />
            </div>
          ) : assetType === "html" ? (
            <div className="critical-slideshow-item html-item">
              <div
                className="critical-html-content"
                dangerouslySetInnerHTML={{
                  __html: currentItem.asset?.url || "",
                }}
              />
            </div>
          ) : (
            <div className="critical-slideshow-item unknown-item">
              <div className="critical-unknown-content">
                Unknown critical content type: {currentItem.asset?.type}
              </div>
            </div>
          )}
        </div>

        {/* Progress indicators (only show if multiple items) */}
        {hasMultipleItems && (
          <div className="critical-progress">
            <div className="progress-info">
              {currentIndex + 1} of {items.length}
            </div>
            <div className="progress-dots">
              {items.map((_, index) => (
                <div
                  key={index}
                  className={`progress-dot ${
                    index === currentIndex ? "active" : ""
                  } ${index < currentIndex ? "completed" : ""}`}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
